import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router";
import { useSelector } from "react-redux";
import { useMemo } from "react";

import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import relativeTimeLabel from "_shared/dates/relative-time-label";
import {
  DashboardCardEyebrow,
  DashboardCardHeader,
  DashboardCardState,
  DashboardPanelCard,
  DashboardRow,
  DashboardRowLink,
  DashboardRowList,
  DashboardRowMarker,
  DashboardRowProse,
  DashboardRowText,
} from "_shared/_views/dashboards/dashboard-card";
import { useGetNotificationsQuery } from "notifications/_api/notifications-api";
import NotificationQuery, {
  NotificationFilterParameters,
  NotificationOrderField,
  NotificationOrderParameters,
} from "notifications/_api/queries/notification-query";
import type { NotificationJSON } from "notifications/_schemas/notification-schema";
import Notification from "notifications/notification";
import { BoxType } from "notifications/notification-box-type";
import { NotificationLink, type NotificationType } from "notifications/notification-type";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";

export const communicationsRoute = "/communications";
export const dashboardRecentActivityLimit = 5;

/**
 * Everything that has already happened, and nothing that is anybody's move.
 *
 * `isActionRequired: false` is `ActionRequired == null` on the server, which takes out every
 * `AcceptOrReject`, every proposal review request and every revision review — the whole of what the
 * queue card is about. `isMessageNotification: false` hands conversations to the unread-messages
 * card, so one message is not counted on two cards of one screen. What is left is past tense:
 * an agreement ended, someone quit a work team, a connection was made, an invitation was declined.
 */
const recentActivityQuery = new NotificationQuery({
  filterParameters: new NotificationFilterParameters({
    boxType: BoxType.Inbox,
    isActionRequired: false,
    isMessageNotification: false,
  }),
  orderParameters: new NotificationOrderParameters({
    orderBy: NotificationOrderField.CreatedDate,
    orderDir: OrderDirection.Descending,
  }),
  paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: dashboardRecentActivityLimit }),
});

/**
 * What a row says.
 *
 * Not `subheader`: every notification in the system that is written with one is also written with a
 * `NotificationAction`, so the rows this card can show never have it — nor a `body` or a
 * `finePrint`. Their prose lives in `message`, which is a single past-tense sentence naming whoever
 * did the thing ("Work Agreement X has ended", "Dana Ruiz has quit the X work team"). `senderName`
 * is no substitute for reading the sentence either: on a team notification it is set to the team
 * leader regardless of who the sentence is about.
 */
export function activityText(notification: Notification): string {
  return notification.message?.value?.trim() ?? "";
}

export function deserializeActivity(notifications: NotificationJSON[]): Notification[] {
  const activity: Notification[] = [];
  for (const json of notifications) {
    const notification = toNotification(json);
    // A row the bundle cannot read is one line of a five-line card; dropping it is better than
    // failing the card, and the full list is a click away in the header either way.
    if (notification && activityText(notification)) activity.push(notification);
  }
  return activity;
}

function toNotification(json: NotificationJSON): Notification | undefined {
  try {
    return Notification.fromJSON(json);
  } catch (error) {
    console.error("Dashboard activity could not deserialize a notification", error);
    return undefined;
  }
}

/**
 * What moved without needing you.
 *
 * The queue card is your move and the bell is an unread queue you drain; this is neither. It is a
 * read-through log of the last few notifications that stays put whether or not you have seen them,
 * because nothing else in the app answers "what happened while I was out" once the bell is empty
 * (#5079).
 *
 * All three surfaces read one stream, which is the thing to watch in review: the filters above are
 * what keeps them from being three versions of one list.
 */
export default function RecentActivityCard() {
  const sessionPermissions = useSelector(getSessionPermissions);
  // The same gate the communications route applies, since every row that has a destination leads
  // into something this permission covers or into work the notification already named.
  const includesCommunications = sessionPermissions?.includesAccessToCommunications === true;

  const notificationsQuery = useGetNotificationsQuery(
    { query: recentActivityQuery },
    { skip: !includesCommunications },
  );

  const activity = useMemo(
    () => deserializeActivity(notificationsQuery.data?.data ?? []),
    [notificationsQuery.data],
  );
  // Read once per render rather than per row, so every row on one card is relative to one instant.
  const now = new Date();

  if (!includesCommunications) return null;

  return (
    <DashboardPanelCard>
      <DashboardCardHeader>
        <Box>
          <DashboardCardEyebrow>
            <HistoryRoundedIcon color="primary" fontSize="small" />
            <Typography color="primary" variant="overline">Activity</Typography>
          </DashboardCardEyebrow>
          <Typography variant="h5" component="h2">Recently</Typography>
        </Box>
        <Button component={RouterLink} to={communicationsRoute} size="small">View all</Button>
      </DashboardCardHeader>
      {notificationsQuery.isLoading && (
        <DashboardCardState role="status">
          <CircularProgress size={20} />
          <Typography variant="body2">Loading recent activity…</Typography>
        </DashboardCardState>
      )}
      {!notificationsQuery.isLoading && notificationsQuery.isError && (
        <DashboardCardState>
          <Typography color="error" variant="body2">Recent activity could not be loaded.</Typography>
        </DashboardCardState>
      )}
      {!notificationsQuery.isLoading && !notificationsQuery.isError && activity.length === 0 && (
        <DashboardCardState>
          <Typography variant="body2">Nothing has moved recently.</Typography>
        </DashboardCardState>
      )}
      {!notificationsQuery.isLoading && !notificationsQuery.isError && activity.length > 0 && (
        <DashboardRowList>
          <Stack spacing={1}>
            {activity.map((notification) => (
              <ActivityRow key={notification.id.value} notification={notification} now={now} />
            ))}
          </Stack>
        </DashboardRowList>
      )}
    </DashboardPanelCard>
  );
}

function ActivityRow(props: Readonly<{ notification: Notification; now: Date }>) {
  const { notification, now } = props;
  const text = activityText(notification);
  const when = relativeTimeLabel(notification.createdDate, now);
  // `NotificationLink` answers with "" for a category with no route — "you are now connected to X"
  // is deliberately one of them — and those rows are plain rather than controls that go nowhere.
  const link = NotificationLink(notification as NotificationType);

  const content = (
    <>
      {/* Quiet whether or not the reader has seen the row. Nothing this card can show is anybody's
          move — `actionRequired` is null on all of it by construction — so there is nothing here
          worth taking attention from the queue beside it (#5123). */}
      <DashboardRowMarker aria-hidden="true" emphasis="quiet">
        <HistoryRoundedIcon fontSize="small" />
      </DashboardRowMarker>
      <DashboardRowText>
        <Typography component="div" variant="subtitle2">
          <DashboardRowProse>{text}</DashboardRowProse>
        </Typography>
        <Typography color="text.secondary" noWrap variant="caption">{when}</Typography>
      </DashboardRowText>
      {link && <ArrowForwardRoundedIcon color="action" fontSize="small" />}
    </>
  );

  if (!link) return <DashboardRow>{content}</DashboardRow>;

  return <DashboardRowLink to={link} aria-label={text}>{content}</DashboardRowLink>;
}
